/* eslint-disable react-refresh/only-export-components */

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";

const isSupabaseConfigured = Boolean(
  import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY,
);

const SKIP_USER_GAME_STATS_KEY = 'cges:skip_user_game_stats';

type AuthContextValue = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      return;
    }

    let unsubscribe: (() => void) | null = null;
    let cancelled = false;

    const run = async () => {
      try {
        // Dynamically import Supabase so the landing page doesn't pay the cost upfront.
        const { supabase } = await import("@/lib/supabase");
        if (cancelled) return;
        if (!supabase) {
          setLoading(false);
          return;
        }

        const {
          data: { subscription },
        } = supabase.auth.onAuthStateChange((_, nextSession) => {
          setSession(nextSession);
          setUser(nextSession?.user ?? null);
        });
        unsubscribe = () => subscription.unsubscribe();

        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        setSession(data.session);
        setUser(data.session?.user ?? null);
      } catch (e) {
        // Keep auth failures non-fatal; pages gate on `user`.
        console.warn("[AuthProvider] init failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    // Push work out of the initial render/paint.
    const win = window as unknown as {
      requestIdleCallback?: (cb: () => void) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const id = typeof win.requestIdleCallback === "function"
      ? win.requestIdleCallback(run)
      : window.setTimeout(run, 1);

    return () => {
      cancelled = true;
      if (typeof win.cancelIdleCallback === "function" && typeof id === "number") {
        win.cancelIdleCallback(id);
      } else {
        window.clearTimeout(id as unknown as number);
      }
      unsubscribe?.();
    };
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    return {
      user,
      session,
      loading,
      signOut: async () => {
        if (!isSupabaseConfigured) return;
        const { supabase } = await import("@/lib/supabase");
        if (!supabase) return;
        await supabase.auth.signOut();
      },
    };
  }, [user, session, loading]);

  // When a user signs in, attempt to rehydrate client-side progress/stats
  // from server-side records (Supabase). This prevents local-only state
  // from overwriting canonical server values and fixes disappearing XP/stats.
  React.useEffect(() => {
    if (!user) return;
    // Run in background; best-effort only.
    (async () => {
      try {
        if (!isSupabaseConfigured) return;
        const mod = await import('@/lib/supabase');
        const sb = (mod as any).supabase as unknown;
        if (!sb) return;
        // Fetch player progress
        try {
          const { data: progressData } = await (sb as any).from('player_progress').select('*').eq('user_id', user.id).single();
          if (progressData) {
            try {
              const mapped = {
                xp: Number(progressData.xp || 0),
                level: Number(progressData.level || 1),
                badges: Array.isArray(progressData.badges) ? progressData.badges : [],
                totalGamesPlayed: Number(progressData.total_games_played || 0),
                totalWins: Number(progressData.total_wins || 0),
                currentStreak: Number(progressData.current_streak || 0),
                bestStreak: Number(progressData.best_streak || 0),
              };
              localStorage.setItem('player-progress', JSON.stringify(mapped));
            } catch (e) {
              // ignore mapping errors
            }
          }
        } catch (e) {
          // ignore
        }

        // Fetch per-game stats (best-effort). If the table is missing or the project
        // doesn't expose this resource, record a local skip flag to avoid repeated 404s.
        try {
          if (typeof localStorage !== 'undefined' && localStorage.getItem(SKIP_USER_GAME_STATS_KEY) === '1') {
            // skip: previously detected missing table
          } else {
            const { data: rows } = await (sb as any).from('user_game_stats').select('*').eq('user_id', user.id);
            if (Array.isArray(rows)) {
              const defaultStats = {
                chess: { wins: 0, losses: 0, draws: 0, gamesPlayed: 0, lastPlayed: null },
                ludo: { wins: 0, losses: 0, draws: 0, gamesPlayed: 0, lastPlayed: null },
                'snakes-ladders': { wins: 0, losses: 0, draws: 0, gamesPlayed: 0, lastPlayed: null },
                uno: { wins: 0, losses: 0, draws: 0, gamesPlayed: 0, lastPlayed: null },
                stratego: { wins: 0, losses: 0, draws: 0, gamesPlayed: 0, lastPlayed: null },
              };
              for (const r of rows) {
                const key = r.game_type as keyof typeof defaultStats;
                if (!key || !(key in defaultStats)) continue;
                (defaultStats as any)[key] = {
                  wins: Number(r.wins || 0),
                  losses: Number(r.losses || 0),
                  draws: Number(r.draws || 0),
                  gamesPlayed: Number(r.games_played || 0),
                  lastPlayed: r.last_played || null,
                };
              }
              try {
                localStorage.setItem('game-statistics', JSON.stringify(defaultStats));
              } catch (e) {
                // ignore
              }
            }
          }
        } catch (e) {
          try {
            const status = (e as any)?.status || (e as any)?.response?.status;
            if (status === 404 && typeof localStorage !== 'undefined') {
              try { localStorage.setItem(SKIP_USER_GAME_STATS_KEY, '1'); } catch {}
            }
          } catch {}
          // ignore
        }
      } catch (e) {
        // swallow rehydrate errors — non-fatal
      }
    })();
  }, [user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuthContext must be used within <AuthProvider>");
  return ctx;
}
